import { ArrowUpRight, Mail, Sparkles } from "lucide-react";

export default function Hero() {
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 lg:pt-36 pb-12 lg:pb-20 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end">
          {/* LEFT — copy */}
          <div className="lg:col-span-7 reveal" style={{ animationDelay: "100ms" }}>
            <div className="flex items-center gap-3 mb-8">
              <span className="pill-tag" data-testid="hero-status-pill">
                <span className="pill-dot"></span>
                Otvorený novým marketingovým príležitostiam
              </span>
            </div>

            <h1
              data-testid="hero-h1"
              className="font-display font-extrabold tracking-tight leading-[0.95] text-[44px] sm:text-6xl lg:text-[112px] xl:text-[128px]"
            >
              <span className="block text-white">Full-Stack</span>
              <span className="block">
                <span className="inline-block text-[#C6F432]">Marketer</span>
                <span className="inline-block ml-3 align-middle">
                  <Sparkles className="inline w-8 h-8 lg:w-12 lg:h-12 text-[#C6F432]" />
                </span>
              </span>
              <span className="block text-white/80 text-[28px] sm:text-4xl lg:text-5xl font-medium mt-4">
                Vízia · Exekúcia · Výsledky
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 max-w-xl text-base lg:text-lg text-white/60 leading-relaxed"
            >
              Som <span className="text-white font-semibold">Mgr. Peter Vaško, PhD.</span> — marketingový manažér,
              stratég a špecialista s presahom do dizajnu a technológií. Spájam komplexné marketingové znalosti s bohatými praktickými skúsenosťami z riadenia marketingu pre B2B, B2C aj B2G značky a technologické projekty.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-cta-portfolio"
                onClick={() => goTo("projekty")}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#C6F432] text-[#0A0A0A] font-semibold text-sm hover:bg-white transition-colors btn-shine"
              >
                Pozrieť portfólio
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </button>
              <button
                data-testid="hero-cta-contact"
                onClick={() => goTo("kontakt")}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/[0.04] text-white border border-white/10 font-semibold text-sm hover:bg-white/[0.08] hover:border-white/20 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Kontakt
              </button>
            </div>

            <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
              <Stat data-testid="hero-stat-1" k="7+" v="rokov praxe" />
              <Stat data-testid="hero-stat-2" k="PhD." v="FMK UCM" />
              <Stat data-testid="hero-stat-3" k="10+" v="kanálov" />
            </div>
          </div>

          {/* RIGHT — photo */}
          <div className="lg:col-span-5 relative reveal" style={{ animationDelay: "300ms" }}>
            <div className="relative">
              <div className="absolute -inset-6 lg:-inset-10 -z-10 rounded-[40px] bg-gradient-to-br from-[#C6F432]/10 via-transparent to-transparent blur-3xl pointer-events-none" />

              {/* Photo wrapper — black bg ensures seamless blend */}
              <div className="relative aspect-square w-full max-w-[560px] mx-auto">
                <img
                  data-testid="hero-photo"
                  src="/assets/me_photo_transparent.png"
                  alt="Mgr. Peter Vaško, PhD."
                  className="hero-photo-mask w-full h-full object-cover object-center"
                  draggable={false}
                />
              </div>

              {/* Floating tag — name card */}
              <div
                data-testid="hero-namecard"
                className="hidden md:block absolute bottom-4 left-2 backdrop-blur-md bg-black/55 border border-white/10 rounded-2xl p-4 pr-6 shadow-[0_8px_40px_rgba(0,0,0,0.45)]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#C6F432] animate-pulse" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">
                      Marketing Manager
                    </div>
                    <div className="text-base font-semibold leading-tight">
                      Mgr. Peter Vaško, PhD.
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating tag — top */}
              <div
                data-testid="hero-tag-top"
                className="hidden md:flex absolute -top-2 right-4 lg:right-0 items-center gap-2 backdrop-blur-md bg-[#C6F432] text-[#0A0A0A] rounded-full px-4 py-2 text-xs font-bold shadow-[0_0_30px_rgba(198,244,50,0.35)]"
              >
                <Sparkles className="w-3.5 h-3.5" /> Marketing · Brand · AI
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v, ...rest }) {
  return (
    <div {...rest}>
      <div className="text-3xl lg:text-4xl font-bold text-white tracking-tight">
        {k}
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1">
        {v}
      </div>
    </div>
  );
}
